import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { useEffect } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InventoryAutoComplete from '@src/components/inventory/InventoryAutoComplete';
import { Controller, useForm, useWatch } from 'react-hook-form';
import MigrationItemSuggestionDTO from '@src/adapters/dto/migration/MigrationItemSuggestionDTO';

interface Props {
  suggestionId: string;
  content: string;
  approved(item: MigrationItemSuggestionDTO): void;
  ignored(): void;
  highlightItem(suggestionId?: string): void;
}

function Suggestion({
  suggestionId,
  content,
  approved,
  ignored,
  highlightItem,
}: Props) {
  const { control, handleSubmit, setValue, reset } =
    useForm<MigrationItemSuggestionDTO>({
      mode: 'all',
      defaultValues: {
        inventory: {
          id: null,
          name: null,
        },
        qtyOpen: 1,
        qtyOnHand: 0,
        isLoan: false,
        alias: true,
        note: '',
      },
    });

  const selectedItem = useWatch({ control, name: 'inventory' });

  useEffect(() => {
    highlightItem(suggestionId);
  }, [suggestionId, highlightItem]);

  const onSubmit = (data: MigrationItemSuggestionDTO) => {
    if (selectedItem) {
      approved(data);
      reset();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box key={suggestionId}>
        <Box
          sx={(theme) => ({
            paddingBottom: theme.spacing(3),
          })}>
          <InventoryAutoComplete
            control={control}
            name="inventory"
            label="Add Item"
            allowAdd={true}
            search={content}
            setValue={setValue}
          />
        </Box>
        <Box sx={{ width: '100%', marginBottom: '20px' }}>
          <Controller
            name="qtyOpen"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                sx={{ width: '100px', marginRight: '8px' }}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: 0,
                }}
                label="Open"
              />
            )}
          />
          <Controller
            name="qtyOnHand"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                sx={{ width: '100px', marginRight: '8px' }}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: 0,
                }}
                label="On hand"
              />
            )}
          />
          <Controller
            name="isLoan"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Mark as Loan"
              />
            )}
          />
        </Box>
        <Box sx={{ width: '100%', marginBottom: '20px' }}>
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="text"
                sx={{ width: '100%' }}
                label="Notes"
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: 0,
                }}
              />
            )}
          />
        </Box>
        <Box sx={{ width: '100%', marginBottom: '20px' }}>
          <Controller
            name="alias"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Create Alias"
              />
            )}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
          }}>
          <Box
            sx={{
              width: '100%',
            }}>
            <Button
              onClick={() => ignored()}
              color="primary"
              variant="outlined"
              sx={(theme) => ({ marginRight: theme.spacing(1) })}>
              Ignore
            </Button>
            <Button
              color="primary"
              variant="outlined"
              type="submit"
              disabled={!selectedItem?.id}>
              <CheckCircleOutlineIcon /> Add Card Item
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

export default Suggestion;
